import {Component, Inject, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {DatePipe} from '@angular/common';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {saveAs} from 'file-saver';
import {KeycloakService} from 'keycloak-angular';
import {forkJoin, of, Subject, throwError} from 'rxjs';
import {catchError, debounceTime, finalize, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {CaseService} from '../../../services/case.service';
import {BankFileService} from '../../../../ent/services/bank-file.service';
import {PaymentsService} from '../../../services/payments.service';
import {CaseDTO} from '../../../models/CaseDTO';
import {PagedRequestDTO} from '../../../../common/models/PagedRequestDTO';
import {SharedService} from '../../../../common/services/shared-service';
import {PagedFilterDTO, PagedFilterDTONew} from '../../../../common/models/PagedFilterDTO';
import {CaseHospitalDTO} from '../../../models/case-hospital/CaseHospitalDTO';
import {CaseMedicalService} from '../../../services/case-medical.service';
import {CaseHospitalProviderLinkDTO} from '../../../models/case-hospital/CaseHospitalProviderLinkDTO';
import {PaymentDTO} from '../../../models/payment/PaymentDTO';
import {CasePaymentLinkDTO} from '../../../models/payment/CasePaymentLinkDTO';
import {CreateNameValueDTO, NameValueDTO} from '../../../../common/models/NameValueDTO';
import {Common, createFieldMap, formatNumber, getSortDirection, stringNullOrEmpty} from '../../../../common/util/Util';
import {AddBankAccountDialogComponent} from '../../../../ent/components/dialogs/add-bank-account-dialog/add-bank-account-dialog.component';
import {ProviderDTO} from '../../../../ent/models/ProviderDTO';
import {ReportingService} from '../../../../other/services/reporting.service';
import {PaymentAuditDTO} from '../../../models/PaymentAuditDTO';
import {LookupValue} from '../../../types/lookup-value.enum';
import {BankAccountDTO} from '../../../../ent/models/BankAccountDTO';
import {BankAccountService} from '../../../../ent/services/bank-account.service';
import {EntRequestBuilder} from '../../../../ent/services/ent-request-builder';
import {EntityTable} from '../../../../common/types/entity-table.enum';
import {PaymentTypeFamily, PaymentTypeOption} from './payment-type.enum';
import {LookupService} from '../../../../common/services/lookup.service';
import {ClaimDisplayDTO} from '../../../models/claim/ClaimDisplayDTO';
import {DateUtil} from '../../../../common/util/DateUtil';
import {TableControl} from '../../../../common/models/TableControl';
import {ClaimService} from '../../../services/claim.service';
import {ProviderService} from '../../../../ent/services/provider.service';
import {PaymentsPermissionService} from '../../../services/permission/payments-permission.service';
import {PagedResponseDTO} from '../../../../common/models/PagedResponseDTO';


const PAGE_SIZE = 50;
const PROVIDER_REMITTANCE_ADVICE = 'Remittance Advice';
const BANK_FILE = 'Bank File';

export interface PreAuthorisationPaymentDialogData {
  paymentDTO: PaymentDTO;
  paymentsByCaseId: number;
  paymentTypeFamily: PaymentTypeFamily;
  paymentTypeOption: PaymentTypeOption;
}

@Component({
  selector: 'app-pre-authorisation-payment-dialog',
  templateUrl: './pre-authorisation-payment-dialog.component.html',
  styleUrls: [
    './pre-authorisation-payment-dialog.component.scss',
    '../../../../../../styles/global/_dialog-styles.scss'
  ]
})
export class PreAuthorisationPaymentDialogComponent implements OnInit {
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly destroyed$ = new Subject();
  readonly caseNameTextChanged$ = new Subject<string>();
  readonly lookupValue = LookupValue;
  readonly PaymentTypeEnum = PaymentTypeFamily;

  isEdit: boolean;
  heading: string;
  userRoles: string[];
  userFirstLastName: string;
  selectedReportType: string;
  reportTypes: string[];
  maxAmount: number;
  amountWarning: string;
  showAmountWarning: boolean;
  showInformationMessage: boolean;
  amountFormControl: FormControl;
  caseNameFormControl: FormControl;
  payeeFormControl: FormControl;
  executionDateFormControl: FormControl;
  isImmediateFormControl: FormControl;
  paymentTypeFormControl: FormControl;
  fromBankAccountFormControl: FormControl;
  relatedTransactionIdFormControl: FormControl;
  formGroup: FormGroup;
  fromBankAccounts: BankAccountDTO[];
  filteredCases: CaseDTO[];
  filteredClaims: ClaimDisplayDTO[];
  selectedCase: CaseHospitalDTO;
  providerLinks: CaseHospitalProviderLinkDTO[];
  editPayment: PaymentDTO;
  today: Date;
  created: Date;
  executionDateHeading: string;

  length = 100;
  pageSize = 10;
  pageNumber = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];
  transactionTableControl: TableControl;

  paymentTypeOption: PaymentTypeOption;
  disabledPaymentType: boolean;
  showTransactionIdField: boolean;
  paymentTypeFamily: PaymentTypeFamily;
  selectedClaim: ClaimDisplayDTO;
  paymentTypeLookUpValues: NameValueDTO[];
  refundPaymentDialogOptions: NameValueDTO[];
  defaultPaymentType: NameValueDTO;

  constructor(
    public dialogRef: MatDialogRef<PreAuthorisationPaymentDialogComponent>,
    private caseService: CaseService,
    private claimService: ClaimService,
    private caseMedicalService: CaseMedicalService,
    private reportingService: ReportingService,
    private bankFileService: BankFileService,
    private datePipe: DatePipe,
    private lookupService: LookupService,
    private paymentService: PaymentsService,
    private keycloakService: KeycloakService,
    private sharedService: SharedService,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private router: Router,
    private bankAccountService: BankAccountService,
    private providerService: ProviderService,
    private paymentsPermissionService: PaymentsPermissionService,
    @Inject(MAT_DIALOG_DATA) public data: PreAuthorisationPaymentDialogData
  ) {
    this.isEdit = data.paymentDTO != null;
    this.paymentTypeFamily = data.paymentTypeFamily;
    this.paymentTypeOption = data.paymentTypeOption;

    this.heading = this.getHeading();
    this.executionDateHeading = this.getExecutionDateHeading();
    this.reportTypes = this.getReportTypes();
    this.selectedReportType = this.reportTypes[0];
    this.today = new Date();
    this.created = this.isEdit ? new Date(this.data.paymentDTO.created) : null;
    this.fromBankAccounts = [];
    this.clearAmountWarnings();

    this.transactionTableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'transactionId',
      'desc',
      'transactionId',
      ''
    );
  }

  ngOnInit(): void {
    this.initFormControls();
    this.initLookUpValues();
    this.initSubscriptions();
    this.userRoles = this.keycloakService.getUserRoles();
    this.keycloakService.loadUserProfile().then(profile => {
      this.userFirstLastName = `${profile.firstName} ${profile.lastName}`;
    });
    if (this.isEdit) {
      this.editPayment = this.data.paymentDTO;
      this.setFormForEdit();
    }
    else if (this.data && this.data?.paymentsByCaseId) {
      // Requirement: Prefill name on dialog opening
      this.getSelectedHospitalCaseBy(this.data?.paymentsByCaseId);
    }
  }

  initFormControls(): void {
    this.caseNameFormControl = new FormControl('', [Validators.required]);
    this.payeeFormControl = new FormControl(null, [Validators.required]);
    this.amountFormControl = new FormControl(null, [Validators.required]);
    this.paymentTypeFormControl = new FormControl(null);
    this.fromBankAccountFormControl = new FormControl(null);
    this.relatedTransactionIdFormControl = new FormControl(null);
    this.executionDateFormControl = new FormControl(this.today, [Validators.required]);
    this.isImmediateFormControl = new FormControl(false, [Validators.required]);
    this.formGroup = this.formBuilder.group({
      caseName: this.caseNameFormControl,
      payee: this.payeeFormControl,
      amount: this.amountFormControl,
      relatedToTransactionId: this.relatedTransactionIdFormControl,
      bankAccount: this.fromBankAccountFormControl,
      executionDate: this.executionDateFormControl,
      isImmediate: this.isImmediateFormControl,
      paymentType: this.paymentTypeFormControl
    });

    this.updateValidatorsAndVisibilityByPaymentDialogType();
  }

  initLookUpValues(): void {
    forkJoin([this.lookupService.getListOfLookupValues(this.lookupValue.PaymentType)])
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          if (response[0]) {
            this.paymentTypeLookUpValues = response[0];
            this.refundPaymentDialogOptions = this.paymentTypeLookUpValues.filter((x: NameValueDTO) => x.name == PaymentTypeOption.ClaimRefund || x.name == PaymentTypeOption.UpfrontRefund);
            this.setDefaultPaymentType();
          }
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
        }
      });
  }

  initSubscriptions(): void {
    this.caseNameTextChanged$.pipe(
      takeUntil(this.destroyed$),
      debounceTime(this.debounceTime),
    )
      .subscribe((searchText: string) => {
          this.filteredCases = [];
          this.getFilteredListOfCases(searchText);
        },
        error => console.log(error));
  }

  onBlurCaseName(): void {
    if (
      this.filteredCases &&
      this.filteredCases.find(
        (item: CaseDTO) => this.getCaseDisplayString(item) === this.caseNameFormControl.value
      ) == null
    ) {
      this.selectedCase = null;
      this.caseNameFormControl.reset();
    }
  }

  onBlurAmountEntered(): void {
    const amount = Number(this.amountFormControl.value);
    if (amount <= this.maxAmount && amount >= 0) {
      this.amountFormControl.setValue(amount.toFixed(2));
    }
    else {
      this.amountFormControl.setValue(null);
    }
  }

  getCaseDisplayString(caseDTO: CaseDTO): string {
    return `${caseDTO.caseId}: ${caseDTO.name} - ${caseDTO.insuredPerson.firstName} ${caseDTO.insuredPerson.lastName}`;
  }

  getProviderDisplayString(provider: ProviderDTO, providerRole: string): string {
    const numberToDisplay = !stringNullOrEmpty(provider.practiceNumber) ?
      provider.practiceNumber : provider.businessLicenseNumber;
    return `${numberToDisplay}: ${provider.name} (${providerRole})`;
  }

  getClaimDisplayString(claim: ClaimDisplayDTO): string {
    const claimNotFound = '';
    if (claim == null) {
      return claimNotFound;
    }
    const dateFormat = DateUtil.dateFormat;
    const transactionId: number = claim.transactionId;
    const receiveDate: string = this.datePipe.transform(claim.receiveDate, dateFormat);
    const amount: string = formatNumber(claim.paidAmount ?? 0);
    const providerName: string = claim.providerName;
    const policyNumber: string = claim.policyNumber;

    const baseDisplayString = `${transactionId} (${receiveDate} - ${amount} - ${providerName} - ${policyNumber})`;

    return transactionId != null ? baseDisplayString : claimNotFound;
  }

  getFromBankAccountDisplayString(bankAccount: BankAccountDTO): string {
    return bankAccount.name + ' - ' + bankAccount.accountNumber;
  }

  getHeading(): string {
    switch (this.paymentTypeFamily) {
    case PaymentTypeFamily.Upfront:
      return 'Upfront Payment';
    case PaymentTypeFamily.Refund:
      return 'Refund Payment';
    case PaymentTypeFamily.Duplicate:
      return 'Duplicate Payment';
    default:
      return 'Default Payment';
    }
  }

  getExecutionDateHeading(): string {
    switch (this.paymentTypeFamily) {
    case PaymentTypeFamily.Upfront:
      return 'Payment Execution Date';
    case PaymentTypeFamily.Refund:
      return 'Payment Refund Date';
    case PaymentTypeFamily.Duplicate:
      return 'Duplicate Payment Date';
    default:
      return 'Date';
    }
  }

  getReportTypes(): string[] {
    switch (this.paymentTypeOption) {
    case PaymentTypeOption.UpfrontRefund:
      return [PROVIDER_REMITTANCE_ADVICE];
    case PaymentTypeOption.UpfrontPayment:
      return [BANK_FILE, PROVIDER_REMITTANCE_ADVICE];
    case PaymentTypeOption.DuplicatePayment:
      return [BANK_FILE];
    case PaymentTypeOption.ClaimRefund:
      return [];
    default:
      return [];
    }
  }

  amountHeading(): string {
    const amountText = this.getAmountText();
    return this.maxAmount < Number.MAX_VALUE ? `${amountText} (Max: ${this.maxAmount ? this.maxAmount.toFixed(2) : 0.00})` : amountText;
  }

  getAmountText(): string {
    switch (this.paymentTypeFamily) {
    case PaymentTypeFamily.Refund:
      return 'Refund Amount';
    case PaymentTypeFamily.Upfront:
      return 'Amount';
    case PaymentTypeFamily.Duplicate:
      return 'Duplicate Amount';
    default:
      return '';
    }
  }


  disableSaveButton(): boolean {
    let disabled = true;
    if (this.isUserAllowed() && !this.isPaid() && this.isFormGroupValid()) {
      disabled = false;
    }
    return disabled;
  }

  disableGenerateButton(): boolean {
    return (
      !this.isPayeeSelected() ||
      !this.isBankAccountPresent() ||
      !this.isRefundPossible() ||
      !this.atLeastOneReportType()
    );
  }

  disabledSaveTooltipMessage(): string {
    if (!this.isUserAllowed()) {
      return `Pre-authorisation team members can submit upfront payments and refunds, claim team members can submit claim refunds and financial managers can do it all`;
    }
    if (this.isPaid()) {
      return 'No changes can be made since the payment reference has been assigned, but the banking file / remittance advice can still be regenerated';
    }
    if (!this.isFormGroupValid()) {
      return 'Not all required fields are completed';
    }
    return '';
  }

  disabledGenerateTooltipMessage(): string {
    let message = '';
    if (!this.isPayeeSelected()) {
      message = 'No payee has been selected';
    }
    else if (!this.isBankAccountPresent()) {
      message = 'The selected payee has no bank account details - add details by clicking on the link in the warning message or via the Enterprise Management tab';
    }
    else if (!this.isRefundPossible()) {
      message = 'Refund amount over the maximum allowable amount';
    }
    else if (!this.atLeastOneReportType()) {
      message = 'No valid file to download for ' + this.paymentTypeOption;
    }
    return message;
  }

  onClickSave(): void {
    const payment: PaymentDTO = this.isEdit ? this.data.paymentDTO : new PaymentDTO();
    const casePayment: CasePaymentLinkDTO = new CasePaymentLinkDTO();
    casePayment.caseId = this.selectedCase.caseDTO.caseId;
    casePayment.provider = CreateNameValueDTO(this.payeeFormControl.value.providerId, this.payeeFormControl.value.name);

    if (this.paymentTypeOption == PaymentTypeOption.ClaimRefund || this.paymentTypeOption == PaymentTypeOption.DuplicatePayment) {
      const claim = this.filteredClaims.find((item: ClaimDisplayDTO) => this.getClaimDisplayString(item) == this.relatedTransactionIdFormControl.value);
      casePayment.relatedToTransactionId = claim != null ? claim.transactionId : null;
    }

    if (this.paymentTypeFamily == PaymentTypeFamily.Refund) {
      casePayment.amount = this.amountFormControl.value * -1;
    }
    else {
      casePayment.amount = this.amountFormControl.value;
    }

    if (this.isEdit) {
      casePayment.createdBy = payment.createdBy;
      casePayment.created = payment.created;
      casePayment.updatedBy = this.userFirstLastName;
      casePayment.updated = new Date();
    }
    else {
      casePayment.createdBy = this.userFirstLastName;
      casePayment.created = new Date();
    }

    const casePayments: CasePaymentLinkDTO[] = [];
    casePayments.push(casePayment);

    payment.casePayments = casePayments;
    if (this.isEdit) {
      payment.updatedBy = this.userFirstLastName;
      payment.updated = new Date();
    }
    else {
      payment.createdBy = this.userFirstLastName;
      payment.created = new Date();
    }
    payment.bankAccountNumber = this.getBankAccountNumberChosen();
    payment.isActive = 1;
    payment.paymentExecutionDate = new Date(this.executionDateFormControl.value);
    payment.isImmediate = this.isImmediateFormControl.value ? 1 : 0;
    payment.paymentType = CreateNameValueDTO(this.paymentTypeFormControl.value?.value, this.paymentTypeFormControl.value?.name);
    // paymentCompletionDate will remain as is

    this.paymentService.savePayment(payment)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this.dialogRef.close(true);
          let message = this.getMessageText();

          if (this.paymentTypeFamily == PaymentTypeFamily.Upfront && !this.isBankAccountPresent()) {
            message += ', but no banking file was generated because no bank account was added for the payee';
          }
          this.sharedService.openSnackBar(message, 'Dismiss');
        })
      )
      .subscribe(
        (response: PaymentDTO) => {
          if (this.paymentTypeFamily == PaymentTypeFamily.Upfront && this.isBankAccountPresent()) {
            this.getBankFileEntries(response.paymentId);
          }
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
        }
      );
  }

  getMessageText(): string {
    switch (this.paymentTypeFamily) {
    case PaymentTypeFamily.Upfront:
      return 'The upfront payment has been saved';
    case PaymentTypeFamily.Refund:
      return 'The refund has been saved';
    case PaymentTypeFamily.Duplicate:
      return 'The duplicate payment has been saved';
    default:
      return 'The payment has been saved';
    }
  }

  onGenerateClick(): void {
    switch (this.selectedReportType) {
    case BANK_FILE: {
      this.getBankFileEntries(this.editPayment.paymentId);
      break;
    }
    case PROVIDER_REMITTANCE_ADVICE: {
      this.generateRemittanceAdviceForPrePayment();
      break;
    }
    }
    this.dialogRef.close();
  }

  openProviderBankAccountDialog(): void {
    const providerId = this.payeeFormControl.value.providerId;
    const dialogRef = this.dialog.open(AddBankAccountDialogComponent, {
      width: '40vw',
      height: '85vh',
      data: {entityId: providerId, entityType: 'Provider'}
    });
    dialogRef.afterClosed().subscribe((result: ProviderDTO) => {
      if (result) {
        const editIndex = this.selectedCase.caseHospitalProviders.findIndex((x: CaseHospitalProviderLinkDTO) => x.provider.providerId = providerId);
        if (editIndex >= 0) {
          this.selectedCase.caseHospitalProviders[editIndex].provider.bankAccount = result.bankAccount;
          this.payeeFormControl.value.bankAccount = result;
        }
      }
    });
  }

  openHospitalCase(): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/claim-management/edit-hospital-case/${this.selectedCase.caseDTO.caseId}`])
    );
    window.open(url, '_blank');
  }

  onPaymentTypeSelectionChanged($event: NameValueDTO): void {
    this.resetRelatedTransactionIDFields();
    this.paymentTypeOption = $event.name as PaymentTypeOption;
    this.paymentTypeFormControl.setValue($event);
    this.updateValidatorsAndVisibilityByPaymentDialogType();
    this.getListOfBankAccounts();
  }

  onCaseSelectionChanged(name: string): void {
    if (this.filteredCases) {
      const caseDTOFound = this.filteredCases.find((item: CaseDTO) => name === this.getCaseDisplayString(item));
      this.getSelectedHospitalCaseBy(caseDTOFound.caseId);
    }
  }

  onTransactionIDSelectionChanged(claimDisplayValue: string): void {
    const claim: ClaimDisplayDTO = this.filteredClaims.find((item: ClaimDisplayDTO) => this.getClaimDisplayString(item) == claimDisplayValue);
    if (!claim) {
      return;
    }
    this.selectedClaim = claim;
    this.relatedTransactionIdFormControl.setValue(this.getClaimDisplayString(claim));
    this.setDefaultBankAccount();
    this.setPayee(claim);
    this.setAmount(this.selectedClaim?.paidAmount ?? 0.00);
  }

  onPayeeSelectionChanged(providerDTO: ProviderDTO): void {
    this.setAmountWarningsByPayee(providerDTO);
  }

  onExecutionDateChanged(): void {
    this.getListOfBankAccounts();
  }

  isPayeeSelected(): boolean {
    return this.payeeFormControl.value != null;
  }

  isBankAccountPresent(): boolean {
    const provider = this.payeeFormControl.value;
    return this.isPayeeSelected() && provider?.bankAccount != null;
  }

  resetCaseRelatedFields(): void {
    this.selectedCase = null;
    this.filteredCases = [];
    this.caseNameFormControl.reset();
    this.payeeFormControl.reset();
    this.fromBankAccountFormControl.reset();
    this.clearAmountWarnings();
    this.resetRelatedTransactionIDFields();
  }

  resetRelatedTransactionIDFields(): void {
    this.selectedClaim = null;
    this.filteredClaims = [];
    this.relatedTransactionIdFormControl.reset();
  }

  minimumExecutionDate(): Date {
    let minimumDate = this.isEdit ? this.created : this.today;
    if (this.paymentTypeOption == PaymentTypeOption.DuplicatePayment || this.paymentTypeOption == PaymentTypeOption.ClaimRefund) {
      minimumDate = this.selectedClaim != null && this.selectedClaim.paymentDate != null ? new Date(this.selectedClaim.paymentDate) : new Date(2000, 1, 1);
    }
    return minimumDate;
  }

  private setPayee(claim: ClaimDisplayDTO) {
    if (claim == null) {
      return;
    }
    this.providerLinks = this.selectedCase.caseHospitalProviders;
    const providerId = claim.providerId;
    this.setPayeeFromProviderId(providerId);
  }

  private setPayeeFromProviderId(providerId: number) {
    this.providerService.getProvider(providerId).subscribe(
      (providerDTO: ProviderDTO) => {
        const specialityExists = !providerDTO && !providerDTO.providerSpecialties && providerDTO.providerSpecialties.length === 0;
        const specialtyDTO = specialityExists ? providerDTO.providerSpecialties[0].providerSpecialtyDTO : null;
        const existingLink = this.providerLinks.find(link => link.provider.providerId === providerDTO.providerId);

        if (!existingLink) {
          const providerLink = new CaseHospitalProviderLinkDTO();
          providerLink.provider = providerDTO;
          providerLink.providerRole = CreateNameValueDTO(0, specialityExists ? specialtyDTO.speciality : 'Unknown Speciality');
          this.providerLinks.push(providerLink);
        }

        this.payeeFormControl.setValue(providerDTO);
        this.setAmountWarningsByPayee(providerDTO);
      }
    );
  }

  private setAmount(amount: number) {
    this.amountFormControl.patchValue(amount);
  }

  private setDefaultBankAccount(): void {
    if (this.fromBankAccountFormControl.value == null || this.fromBankAccountFormControl.value == '') {
      if (this.selectedClaim && (this.paymentTypeOption == PaymentTypeOption.DuplicatePayment || this.paymentTypeOption == PaymentTypeOption.ClaimRefund)) {
        this.setFromAccountNumber(this.selectedClaim.paidFromBankAccount);
        this.sharedService.openSnackBar('Bank account defaulted to that of the related transaction ID', 'Dismiss');
      }
      else if (this.paymentTypeOption == PaymentTypeOption.UpfrontPayment || this.paymentTypeOption == PaymentTypeOption.UpfrontRefund) {
        this.setFromAccountNumber(this.getFromAccountNumberForUpfrontPayment());
      }
    }
  }

  private setFromAccountNumber(accountNumber: string): void {
    if (accountNumber !== 'UNKNOWN') {
      const findMatchingBankAccount: BankAccountDTO = this.findBankAccountByAccountNumber(accountNumber);
      if (findMatchingBankAccount) {
        const displayString: string = this.getFromBankAccountDisplayString(findMatchingBankAccount);
        this.fromBankAccountFormControl.patchValue(displayString);
      }
    }
  }

  private findBankAccountByAccountNumber(accountNumber: string): BankAccountDTO {
    return this.fromBankAccounts.find((account: BankAccountDTO) => account.accountNumber === accountNumber);
  }

  private isRefundPossible(): boolean {
    if (!(this.paymentTypeFamily == PaymentTypeFamily.Refund)) {
      return true; // not a refund so return true
    }
    return this.maxAmount > 0 && this.maxAmount < Number.MAX_VALUE;
  }

  private atLeastOneReportType() {
    return this.getReportTypes() != null && this.getReportTypes().length > 0;
  }

  private getBankAccountNumberChosen(): string {
    const bankAccountDTO = this.fromBankAccounts.find((account: BankAccountDTO) => this.getFromBankAccountDisplayString(account) === this.fromBankAccountFormControl?.value);
    return bankAccountDTO?.accountNumber;
  }

  private clearAmountWarnings(): void {
    this.maxAmount = Number.MAX_VALUE;
    this.amountWarning = '';
    this.showAmountWarning = false;
    this.showInformationMessage = false;
  }

  private setAmountWarningsByPayee(providerDTO: ProviderDTO): void {
    if (!providerDTO || !this.selectedCase) {
      return;
    }
    const caseId = this.selectedCase.caseDTO.caseId;
    const providerId = providerDTO.providerId;
    this.setAmountWarningsByCaseAndProvider(caseId, providerId);
  }

  private setAmountWarningsByCaseAndProvider(caseId: number, providerId: number) {
    this.paymentService.getPaymentAuditByCaseId(caseId, providerId)
      .pipe(take(1))
      .subscribe(
        (paymentAuditDTOs: PaymentAuditDTO[]) => {
          this.setMaxAmount(paymentAuditDTOs);
          this.setAmountWarningStatus(paymentAuditDTOs);
          if (this.amountFormControl.value > this.maxAmount) {
            this.amountFormControl.setValue(null);
          }
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
        }
      );
  }

  private setMaxAmount(paymentAuditDTOs: PaymentAuditDTO[]) {
    if (paymentAuditDTOs && paymentAuditDTOs?.length) {
      if (this.paymentTypeFamily === PaymentTypeFamily.Upfront) {
        paymentAuditDTOs.sort((a: PaymentAuditDTO, b: PaymentAuditDTO) => {
            if (a.transactionId > b.transactionId) {
              return -1;
            }
            if (a.transactionId < b.transactionId) {
              return 1;
            }
            return 0;
          }
        );
        const existingLinkedAuthorisation = this.isEdit ? paymentAuditDTOs.find(x => Math.round(x.authorisationAmount) == Math.round(this.editPayment.amount)) : null;
        if (existingLinkedAuthorisation != null) {
          this.maxAmount = Math.round((existingLinkedAuthorisation.authorisationAmount + Number.EPSILON) * 100) / 100;
        }
        else if (paymentAuditDTOs[0].transactionId != null) {
          this.maxAmount = Math.round((paymentAuditDTOs[0].authorisationAmount + Number.EPSILON) * 100) / 100;
        }
        else {
          this.maxAmount = Number.MAX_VALUE;
        }
      }
      else if (this.paymentTypeOption === PaymentTypeOption.UpfrontRefund) {
        this.maxAmount = paymentAuditDTOs[0].preAuthorisationAmount + Number(this.amountFormControl.value);
      }
    }
    else if (this.paymentTypeOption === PaymentTypeOption.ClaimRefund || this.paymentTypeOption === PaymentTypeOption.DuplicatePayment) {
      this.maxAmount = this.selectedClaim?.paidAmount ?? 0;
    }
  }

  private setAmountWarningStatus(paymentAuditDTOs: PaymentAuditDTO[]) {
    const preAuthorisationAmount = paymentAuditDTOs.find((x: PaymentAuditDTO) => x.preAuthorisationAmount > 0)?.preAuthorisationAmount;
    if (preAuthorisationAmount > 0 && this.amountFormControl.value != preAuthorisationAmount) {
      this.showAmountWarning = !this.isPaid() && this.amountFormControl.value != 0;
      const warningAmount = this.paymentTypeFamily == PaymentTypeFamily.Duplicate || this.paymentTypeOption === PaymentTypeOption.ClaimRefund ? (this.selectedClaim?.paidAmount ?? 0) : paymentAuditDTOs[0].preAuthorisationAmount;
      this.amountWarning = this.getAmountWarningText(warningAmount);
    }
    else {
      this.showAmountWarning = false;
      this.amountWarning = '';
    }
  }

  private getAmountWarningText(amount: number): string {
    switch (this.paymentTypeOption) {
    case PaymentTypeOption.UpfrontRefund:
    case PaymentTypeOption.UpfrontPayment:
      return `The provider has already been paid a net upfront amount of ${amount > 0 ? amount : 0}.`;
    case PaymentTypeOption.ClaimRefund:
    case PaymentTypeOption.DuplicatePayment:
      return `The provider was paid an amount of ${amount > 0 ? amount : 0} on the original claim.`;
    default:
      return '';
    }
  }

  private getListOfBankAccounts(): void {
    if (!this.selectedCase) {
      return;
    }

    this.getListOfBankAccountsObs$()
      .pipe(take(1))
      .subscribe({
        next: (response: { success: boolean }) => {
          if (response.success) {
            this.handleDefaultBankAccount();
          }
        },
        error: (errorResponse) => {
          const error = errorResponse.error || 'Failed to fetch bank accounts';
          this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
        }
      });
  }

  private getListOfBankAccountsObs$() {
    const caseDTO: CaseDTO = this.selectedCase.caseDTO;
    let payerId = caseDTO.product.payer.value;
    const processingClassId = caseDTO.processingClass.value;

    // TODO: Update product to contain multiple payers with effective dates (PBM-1205), and remove this hard-coded method
    if (caseDTO.product.name.includes('PSS') || caseDTO.product.name.includes('RFLI')) {
      const switchoverDate = new Date('2023-10-01');
      const paymentDate = this.executionDateFormControl.value ? new Date(this.executionDateFormControl.value) : this.today;
      payerId = paymentDate >= switchoverDate ? 3 : 2;
    }

    let paymentDefaultName = this.getPaymentDefaultName();
    paymentDefaultName = paymentDefaultName ? paymentDefaultName : null;
    const pagedRequestDTO: PagedRequestDTO = EntRequestBuilder.getListOfClientBankAccounts(String(payerId), paymentDefaultName, processingClassId, null, null);
    return this.bankAccountService.getListOfBankAccounts(pagedRequestDTO)
      .pipe(
        take(1),
        tap((response: PagedResponseDTO<BankAccountDTO>) => {
          this.fromBankAccounts = response.resultList || [];
        }),
        map(_ => ({success: true})),
        catchError(error => {
          return [{success: false, error}];
        })
      );
  }

  private handleDefaultBankAccount() {
    if (this.isEdit) {
      this.setFromAccountNumber(this.editPayment.bankAccountNumber);
    }
    else {
      this.setDefaultBankAccount();
    }
  }

  private getPaymentDefaultName(): string | null {
    const paymentType = this.paymentTypeFormControl.value;
    if (paymentType) {
      // Bank account search dynamic
      return paymentType.name && paymentType.name.includes('Upfront') ? 'Upfront' : null;
    }
    else {
      return null;
    }
  }

  private getFilteredListOfCases(searchText: string): void {
    if (!isNaN(Number(searchText))) {
      this.getFilteredListOfCasesByFilterField('caseId', searchText);
    }
    this.getFilteredListOfCasesByFilterField('name', searchText);
    this.getFilteredListOfCasesByFilterField('insuredPersonByInsuredPersonId.lastName', searchText);
  }

  private getFilteredListOfCasesByFilterField(filterField: string, searchText: string): void {
    const pagedFilterDTO = new PagedFilterDTO();
    pagedFilterDTO.name = filterField;
    pagedFilterDTO.value = searchText;

    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblCase;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = PAGE_SIZE;

    pagedRequestDTO.fieldMap = {};
    if (filterField.includes('.')) {
      const complexPagedFilterDTO = new PagedFilterDTO();
      complexPagedFilterDTO.name = filterField;
      complexPagedFilterDTO.value = searchText;
      pagedRequestDTO.fieldMap[filterField.split('.')[0]] = complexPagedFilterDTO;
    }
    else {
      pagedRequestDTO.fieldMap = createFieldMap(
        filterField,
        searchText
      );
    }

    this.getListOfCases(pagedRequestDTO);
  }

  private getBankFileEntries(paymentId: number) {
    this.bankFileService.getBankFileEntriesPrAuthorisationFnb(paymentId, 'case')
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(
        (response) => {
          if (response != null && response.length > 0) {
            const accountNumber: string = this.getBankAccountNumberChosen();
            this.bankFileService.generateBankFile(response, accountNumber, paymentId);
            this.sharedService.openSnackBar(
              `The bank file has generated successfully`,
              'Dismiss'
            );
          }
          else {
            this.sharedService.openSnackBar(
              `The banking file has failed to generate.`,
              'Dismiss'
            );
          }
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getListOfCases(pagedRequestDTO: PagedRequestDTO): void {
    this.caseService.getListOfCases(pagedRequestDTO)
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(
        (response) => {
          if (response && response.resultList) {
            this.filteredCases = this.filteredCases.concat(response.resultList);
          }
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
        }
      );
  }

  private getSelectedHospitalCaseBy(caseId: number): void {
    this.getSelectedHospitalCaseByObs$(caseId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (_: any) => {
          // Handle "add" case in this scenario
          this.handleDefaultBankAccount();
        }
      });
  }

  private getSelectedHospitalCaseByObs$(caseId: number) {
    return this.caseMedicalService.getCaseMedicalWithoutFilesBy(caseId)
      .pipe(
        take(1),
        map((response: CaseHospitalDTO) => {
          if (!response) {
            const message = `Please select a medical case instead of a funeral case`;
            this.sharedService.openSnackBar(message, 'Dismiss');
            return throwError(() => new Error(message)); // Terminate the stream with an error
          }
          this.selectedCase = response;
          this.providerLinks = this.selectedCase.caseHospitalProviders;
          this.caseNameFormControl.setValue(this.getCaseDisplayString(this.selectedCase.caseDTO));
          return of(null);
        }),
        switchMap((_: any) => {
          return this.getListOfTransactionsObs$()
            .pipe(
              take(1),
              catchError((_err: any) => of(null))
            );
        }),
        switchMap((_: any) => {
          return this.getListOfBankAccountsObs$()
            .pipe(
              take(1),
              catchError((_err: any) => of(null))
            );
        }),
        map((resp) => {
          return ({success: true});
        }),
        catchError(error => {
          return [{success: false, error}];
        })
      );
  }

  private isUserAllowed(): boolean {
    return this.paymentsPermissionService.isUserAllowedToPayment(this.paymentTypeFamily, this.paymentTypeOption);
  }

  private isFormGroupValid(): boolean {
    return this.formGroup.valid;
  }

  private isPaid(): boolean {
    return this.isEdit && !!this.editPayment?.paymentReference;
  }

  private setFormForEdit(): void {
    const firstCasePayment: CasePaymentLinkDTO = this.editPayment.casePayments[0];

    this.getSelectedHospitalCaseByObs$(firstCasePayment.caseId)
      .pipe(take(1))
      .subscribe({
        next: (_: any) => {
          // Set Relational Transactional ID
          this.setRelatedTransactionId(firstCasePayment);

          // Set Payee
          if (this.providerLinks != null && this.providerLinks.length > 0 && this.editPayment.casePayments != null && this.editPayment.casePayments.length > 0) {
            this.setPayeeFromProviderId(this.editPayment.casePayments[0].provider.value);
          }
          // Handle "edit" case in this scenario
          this.handleDefaultBankAccount();
        }
      });

    this.onBlurAmountEntered();

    this.setAmount(Math.abs(firstCasePayment.amount));

    // Set Execution Date
    this.executionDateFormControl.setValue(new Date(this.data.paymentDTO.paymentExecutionDate));
    // Set Immediate Indicator
    this.isImmediateFormControl.setValue(this.data.paymentDTO.isImmediate == 1);
  }

  private setRelatedTransactionId(casePayment: CasePaymentLinkDTO) {
    if (this.paymentTypeFamily == PaymentTypeFamily.Refund && this.paymentTypeOption === PaymentTypeOption.ClaimRefund || this.paymentTypeFamily == PaymentTypeFamily.Duplicate && this.paymentTypeOption === PaymentTypeOption.DuplicatePayment) {
      const relatedTransactionId = casePayment?.relatedToTransactionId;
      if (!relatedTransactionId) {
        return;
      }

      const relatedTransactionIDClaim: ClaimDisplayDTO = this.filteredClaims.find((claimDTO: ClaimDisplayDTO) => claimDTO.transactionId == this.editPayment.casePayments[0].relatedToTransactionId);
      if (relatedTransactionIDClaim) {
        this.onTransactionIDSelectionChanged(this.getClaimDisplayString(relatedTransactionIDClaim));
      }
    }
  }

  private getFromAccountNumberForUpfrontPayment(): string {
    return this.fromBankAccounts != null && this.fromBankAccounts.length > 0 ? this.fromBankAccounts[0].accountNumber.toString() : 'UNKNOWN';
  }

  private generateRemittanceAdviceForPrePayment(): void {
    this.reportingService.generateReport(
      '/Reports/Prepayment_Remittance_Advice',
      'pdf',
      `payment_id=${this.editPayment.paymentId}&case_id=${this.selectedCase.caseDTO.caseId}`
    )
      .pipe(
        take(1)
      )
      .subscribe(
        (response) => {
          saveAs(response, `remittance_advice_prepayment_${this.editPayment.paymentId}_${this.datePipe.transform(new Date(), 'yyyyMMdd_HHmm')}.pdf`);
        },
        (_) => {
          this.sharedService.openSnackBar('There was an error generating the report', 'Dismiss');
        },
      );
  }

  private getListOfTransactionsObs$() {
    return this.claimService.getListOfClaims(this.getPagedRequestDTO(this.transactionTableControl))
      .pipe(
        take(1),
        tap((response: PagedResponseDTO<ClaimDisplayDTO>) => {
          this.filteredClaims = response.resultList || [];
        }),
        map(response => ({success: true, claims: response.resultList || []})),
        catchError(error => {
          return [{success: false, error}];
        })
      );
  }

  private getPagedRequestDTO(tableControl: TableControl): PagedRequestDTO {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblTransaction;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = getSortDirection(tableControl.sortDirection);
    pagedRequestDTO.fieldMap = {};

    const value = tableControl?.filterValue?.trim();

    const transactionTypePagedFilterDTO = new PagedFilterDTONew();
    transactionTypePagedFilterDTO.name = 'transactionTypeId';
    transactionTypePagedFilterDTO.value = '2';
    transactionTypePagedFilterDTO.operator = '==';
    pagedRequestDTO.fieldMap[`transactionTypeId`] = transactionTypePagedFilterDTO;

    const processingStatusPagedFilterDTO = new PagedFilterDTONew();
    processingStatusPagedFilterDTO.name = 'processingStatusId';
    processingStatusPagedFilterDTO.value = this.paymentTypeOption == PaymentTypeOption.ClaimRefund ? '15' : '9,10';
    processingStatusPagedFilterDTO.operator = 'in';
    pagedRequestDTO.fieldMap[`processingStatusId`] = processingStatusPagedFilterDTO;

    const paidAmountPagedFilterDTO = new PagedFilterDTONew();
    paidAmountPagedFilterDTO.name = 'paidAmount';
    paidAmountPagedFilterDTO.value = '0';
    paidAmountPagedFilterDTO.operator = '>=';
    pagedRequestDTO.fieldMap[`paidAmount`] = paidAmountPagedFilterDTO;

    if (this.selectedCase) {
      const insuredPersonIdPagedFilterDTO = new PagedFilterDTONew();
      insuredPersonIdPagedFilterDTO.name = 'insuredPersonId';
      insuredPersonIdPagedFilterDTO.value = String(this.selectedCase?.caseDTO?.insuredPerson?.insuredPersonId);
      insuredPersonIdPagedFilterDTO.logicGate = 'AND';
      insuredPersonIdPagedFilterDTO.bracket = '(';
      pagedRequestDTO.fieldMap[`insuredPersonId`] = insuredPersonIdPagedFilterDTO;

      const policyIdPagedFilterDTO = new PagedFilterDTONew();
      policyIdPagedFilterDTO.name = 'policyId';
      policyIdPagedFilterDTO.value = String(this.selectedCase?.caseDTO?.policy?.policyId);
      policyIdPagedFilterDTO.logicGate = 'OR';
      policyIdPagedFilterDTO.bracket = '';
      pagedRequestDTO.fieldMap[`policyId`] = policyIdPagedFilterDTO;

      const productIdPagedFilterDTO = new PagedFilterDTONew();
      productIdPagedFilterDTO.name = 'productId';
      productIdPagedFilterDTO.value = String(this.selectedCase?.caseDTO?.product?.productId);
      productIdPagedFilterDTO.logicGate = 'OR';
      productIdPagedFilterDTO.bracket = ')';
      pagedRequestDTO.fieldMap[`productId`] = productIdPagedFilterDTO;

    }

    return pagedRequestDTO;
  }

  private updateValidatorsAndVisibilityByPaymentDialogType(): void {
    if (this.paymentTypeFamily == PaymentTypeFamily.Upfront) {
      this.disabledPaymentType = true;
      this.showTransactionIdField = false;
      this.relatedTransactionIdFormControl.setValidators([]);
      this.payeeFormControl.enable();
      this.amountFormControl.enable();
      this.fromBankAccountFormControl.disable();
      this.isImmediateFormControl.enable();
    }
    else if (this.paymentTypeFamily == PaymentTypeFamily.Duplicate) {
      this.disabledPaymentType = true;
      this.showTransactionIdField = true;
      this.relatedTransactionIdFormControl.setValidators([Validators.required]);
      this.payeeFormControl.disable();
      this.amountFormControl.disable();
      this.fromBankAccountFormControl.enable();
      this.isImmediateFormControl.disable();
    }
    else if (this.paymentTypeFamily == PaymentTypeFamily.Refund) {
      // User has option to select type of refund from ddn in case of refund
      this.disabledPaymentType = false;
      if (this.paymentTypeOption == PaymentTypeOption.ClaimRefund) {
        this.showTransactionIdField = true;
        this.relatedTransactionIdFormControl.setValidators([Validators.required]);
        this.payeeFormControl.disable();
        this.amountFormControl.disable();
        this.fromBankAccountFormControl.enable();
        this.isImmediateFormControl.disable();
      }
      else if (this.paymentTypeOption === PaymentTypeOption.UpfrontRefund) {
        this.showTransactionIdField = false;
        this.relatedTransactionIdFormControl.setValidators([]);
        this.payeeFormControl.enable();
        this.amountFormControl.enable();
        this.fromBankAccountFormControl.disable();
        this.isImmediateFormControl.enable();
      }
    }
    this.relatedTransactionIdFormControl.updateValueAndValidity();
    this.showInformationMessage = this.paymentTypeOption == PaymentTypeOption.UpfrontPayment || this.paymentTypeOption == PaymentTypeOption.UpfrontRefund;
  }

  private setDefaultPaymentType(): void {
    if (this.isEdit) {
      this.defaultPaymentType = this.paymentTypeLookUpValues.find((item: NameValueDTO) => item.name === this.paymentTypeOption);
    }
    else {
      if (this.paymentTypeFamily == PaymentTypeFamily.Upfront) {
        this.defaultPaymentType = this.paymentTypeLookUpValues.find((item: NameValueDTO) => item.name === PaymentTypeOption.UpfrontPayment);
      }
      else if (this.paymentTypeFamily == PaymentTypeFamily.Refund) {
        this.defaultPaymentType = this.paymentTypeLookUpValues.find((item: NameValueDTO) => item.name === PaymentTypeOption.UpfrontRefund);
      }
      else if (this.paymentTypeFamily == PaymentTypeFamily.Duplicate) {
        this.defaultPaymentType = this.paymentTypeLookUpValues.find((item: NameValueDTO) => item.name === PaymentTypeOption.DuplicatePayment);
      }
    }
    this.paymentTypeFormControl.setValue(this.defaultPaymentType);
  }


  compareProviders(provider1: ProviderDTO, provider2: ProviderDTO): boolean {
    return provider1 && provider2 ? provider1.providerId === provider2.providerId : provider1 === provider2;
  }

}
